import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import { Button, Modal, useToast } from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";
import { useSignedInUser } from "../hooks/useSignedInUser";

interface LogoutConfirmModalProps {
  open: boolean;
  /** Dismissing the dialog. Ignored while the sign-out is in flight. */
  onClose: () => void;
}

/**
 * "Log out?" — asked the same way wherever it is asked from.
 *
 * Two places offer it now: the rail's account block, and the Settings screen's
 * Accounts tab. Signing out is one action with one confirmation, so the dialog
 * and the sign-out itself live here rather than being written out twice — a
 * second copy is how one of them quietly stops matching the other.
 *
 * It owns the sign-out rather than taking it as a prop, because the two cannot
 * be separated: the dialog must not be dismissable while Clerk is ending the
 * session, and only whatever is doing the ending knows when that is.
 *
 * Must be used inside a Clerk provider and a router.
 */
export const LogoutConfirmModal: React.FC<LogoutConfirmModalProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { fullName } = useSignedInUser();
  const toast = useToast();

  const [signingOut, setSigningOut] = useState(false);

  // `useSignedInUser` walks Clerk's fields down to the email address; the
  // generic is for the moment before Clerk has loaded, and it is only ever a
  // flash — every screen offering this is already behind `RequireAuth`.
  const displayName = fullName || "your account";

  const handleLogout = async () => {
    setSigningOut(true);
    try {
      // Clerk ends the session and clears its cookie; the callback runs after
      // that, so RequireAuth cannot race us back here. The dialog is left open
      // until then — closing it first would flash the app behind it.
      await signOut(() => navigate(ROUTES.login, { replace: true }));
    } catch (error) {
      toast.error("Could not log out", "Please try again.");
      console.error("Sign-out error:", error);
      setSigningOut(false);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      title="Log out"
      onClose={() => {
        // A sign-out in flight must not be dismissable: the callback that
        // navigates has not run yet.
        if (!signingOut) onClose();
      }}
      className="max-w-[440px]"
      footer={
        <>
          <Button
            variant="secondary"
            size="md"
            disabled={signingOut}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="md"
            loading={signingOut}
            onClick={handleLogout}
          >
            Log out
          </Button>
        </>
      }
    >
      <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
        You&rsquo;ll be signed out of {displayName} and returned to the login
        screen.
      </p>
    </Modal>
  );
};

export default LogoutConfirmModal;
